'use client'

import {
  FullBleedImage,
  OfferFrame,
  SafeText,
  clampIndex,
  getDesign,
  getOfferCopy,
  getOfferVisual,
  type OfferPackProps,
} from '../shared/OfferPackCore'

const bg = [
  'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1556761175-4b46a572b786?q=80&w=1400&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1400&auto=format&fit=crop',
]

const total = 5
const sans = '"Montserrat", "Inter", Arial, sans-serif'
const body = '"Inter", "Helvetica Neue", Arial, sans-serif'

export default function EventConversionOfferPack({
  draft,
  activeIndex = 0,
  onActiveIndexChange,
}: OfferPackProps) {
  const index = clampIndex(activeIndex, total)
  const design = getDesign(draft, '#FFB000')
  const accent = design.accent
  const purple = '#8B5CF6'
  const dark = '#100B1F'
  const text = '#FFFFFF'

  const copy = getOfferCopy(draft, index, {
    eyebrow: 'Live event offer',
    headline: 'Entra al evento antes de que cierre el registro',
    subheadline: 'Una sesión diseñada para salir con claridad, plan y próximos pasos accionables.',
    offer: 'Registro premium + recursos incluidos',
    proof: 'Aprende en vivo. Aplica al instante.',
    urgency: 'Registro cierra pronto',
    cta: 'Reservar mi lugar',
    bullets: ['Sesión en vivo', 'Framework descargable', 'Q&A privado'],
  })

  return (
    <OfferFrame
      activeIndex={index}
      total={total}
      onActiveIndexChange={onActiveIndexChange}
      text={text}
      className="bg-[#100B1F]"
    >
      {index === 0 ? (
        <>
          <FullBleedImage {...getOfferVisual(draft, index, bg[0], { overlay: 'black', opacity: 0.46 })} />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_18%,rgba(139,92,246,0.48),transparent_34%),linear-gradient(90deg,rgba(16,11,31,0.96),rgba(16,11,31,0.42),rgba(16,11,31,0.1))]" />
          <div className="absolute left-8 top-8 rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-black" style={{ background: accent, fontFamily: sans }}>
            {copy.eyebrow}
          </div>
          <div className="absolute bottom-16 left-8 max-w-[76%] text-white">
            <SafeText className="text-[clamp(42px,10vw,68px)] font-black uppercase leading-[0.9] tracking-[-0.055em]" style={{ fontFamily: sans }}>
              {copy.headline}
            </SafeText>
            <SafeText className="mt-5 max-w-[72%] text-[15px] leading-[1.42] text-white/76" style={{ fontFamily: body }}>
              {copy.subheadline}
            </SafeText>
          </div>
        </>
      ) : null}

      {index === 1 ? (
        <>
          <div className="absolute inset-0" style={{ background: dark }} />
          <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full blur-3xl" style={{ background: `${purple}66` }} />
          <div className="absolute left-8 top-8 text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: accent, fontFamily: sans }}>
            Incluye
          </div>
          <SafeText className="absolute left-8 top-20 max-w-[66%] text-[48px] font-black uppercase leading-[0.94] tracking-[-0.05em] text-white" style={{ fontFamily: sans }}>
            {copy.offer || 'Todo para aprovechar la sesión en vivo.'}
          </SafeText>
          <div className="absolute bottom-9 left-8 right-8 grid grid-cols-3 gap-3">
            {(copy.bullets.length ? copy.bullets : ['Sesión', 'Plantilla', 'Q&A']).slice(0, 3).map((item, idx) => (
              <div key={idx} className="min-h-[136px] rounded-[28px] border border-white/14 bg-white/[0.06] p-5 backdrop-blur">
                <div className="text-[12px] font-black" style={{ color: accent, fontFamily: sans }}>0{idx + 1}</div>
                <SafeText className="mt-6 text-[17px] font-bold leading-[1.08] text-white" style={{ fontFamily: body }}>
                  {item}
                </SafeText>
              </div>
            ))}
          </div>
        </>
      ) : null}

      {index === 2 ? (
        <>
          <FullBleedImage {...getOfferVisual(draft, index, bg[2], { overlay: 'black', opacity: 0.58 })} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#100B1F] via-[#100B1F]/45 to-transparent" />
          <div className="absolute left-8 top-10 max-w-[76%] text-white">
            <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: accent, fontFamily: sans }}>Por qué asistir</div>
            <SafeText className="mt-6 text-[50px] font-black uppercase leading-[0.92] tracking-[-0.055em]" style={{ fontFamily: sans }}>
              {copy.proof || 'Sales con una decisión tomada y un plan para ejecutarla.'}
            </SafeText>
          </div>
          <div className="absolute bottom-10 left-8 right-8 flex items-center justify-between border-t border-white/18 pt-5 text-white/70" style={{ fontFamily: body }}>
            <span>Framework</span>
            <span>Live Q&A</span>
            <span>Action plan</span>
          </div>
        </>
      ) : null}

      {index === 3 ? (
        <>
          <div className="absolute inset-0" style={{ background: '#160F2A' }} />
          <div className="absolute inset-y-0 left-0 w-[42%] overflow-hidden">
            <FullBleedImage {...getOfferVisual(draft, index, bg[3], { overlay: 'black', opacity: 0.28 })} />
          </div>
          <div className="absolute right-8 top-10 max-w-[48%] text-white">
            <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: accent, fontFamily: sans }}>Urgencia</div>
            <SafeText className="mt-6 text-[48px] font-black uppercase leading-[0.92] tracking-[-0.055em]" style={{ fontFamily: sans }}>
              {copy.urgency || 'El registro cierra antes del evento.'}
            </SafeText>
            <SafeText className="mt-6 text-[15px] leading-[1.45] text-white/66" style={{ fontFamily: body }}>
              {copy.body || copy.subheadline}
            </SafeText>
          </div>
          <div className="absolute bottom-9 right-8 rounded-full px-5 py-3 text-[11px] font-black uppercase tracking-[0.18em] text-black" style={{ background: accent, fontFamily: sans }}>
            Últimos lugares
          </div>
        </>
      ) : null}

      {index === 4 ? (
        <>
          <FullBleedImage {...getOfferVisual(draft, index, bg[4], { overlay: 'black', opacity: 0.52 })} />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(255,176,0,0.25),transparent_35%),linear-gradient(0deg,rgba(16,11,31,0.92),rgba(16,11,31,0.35))]" />
          <div className="absolute inset-x-8 top-10 text-center text-white">
            <div className="text-[11px] font-black uppercase tracking-[0.28em] text-white/60" style={{ fontFamily: sans }}>Reserva ahora</div>
            <SafeText className="mx-auto mt-8 max-w-[82%] text-[58px] font-black uppercase leading-[0.88] tracking-[-0.06em]" style={{ fontFamily: sans }}>
              {copy.cta || 'Asegura tu lugar'}
            </SafeText>
          </div>
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 rounded-full px-7 py-4 text-[12px] font-black uppercase tracking-[0.18em] text-black" style={{ background: accent, fontFamily: sans }}>
            Registrarme
          </div>
        </>
      ) : null}
    </OfferFrame>
  )
}
